import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/naming-convention */
/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { format } from 'date-fns';
import { useCallback, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { classNames } from '../_common/defaultImports';
import { IconButton } from '../button';
import { forwardRef } from '../helpers';
import { ArrowLeftIconOutline, ArrowRightIconOutline, CalendarIconOutline, ChevronDownIconOutline, } from '../icons';
import { TextInput } from '../text-input';
import { Typography } from '../typography';
export const DatePicker = forwardRef(function DatePicker(props, ref) {
    const { reactDatePickerProps, textInputProps, disabled, className } = props;
    const classes = classNames(`ndl-datepicker`, className, {});
    const datetimeRef = useRef(null);
    const [picker, setPicker] = useState('day');
    const headerAction = useCallback((action) => {
        picker === action ? setPicker('day') : setPicker(action);
    }, [picker]);
    const CustomHeader = useCallback((props) => {
        const { decreaseMonth, increaseMonth, increaseYear, decreaseYear, nextMonthButtonDisabled, nextYearButtonDisabled, prevMonthButtonDisabled, prevYearButtonDisabled, } = props;
        const prevCallback = picker === 'year' ? decreaseYear : decreaseMonth;
        const nextCallback = picker === 'year' ? increaseYear : increaseMonth;
        const prevDisabled = picker === 'year' ? prevYearButtonDisabled : prevMonthButtonDisabled;
        const nextDisabled = picker === 'year' ? nextYearButtonDisabled : nextMonthButtonDisabled;
        return (_jsxs("div", { className: "ndl-datepicker-header", children: [_jsxs("div", { className: "ndl-datepicker-selects", children: [_jsx("button", { className: "ndl-datepicker-header", onClick: () => headerAction('month'), children: _jsxs("div", { className: "n-flex n-items-center", children: [_jsx(Typography, { variant: "label", className: "n-p-token-4", children: format(props.date, 'MMMM') }), _jsx(ChevronDownIconOutline, { className: classNames('ndl-datepicker-chevron', {
                                            'n-rotate-180': picker === 'month',
                                        }), "aria-label": "Chevron icon" })] }) }), _jsx("button", { className: "ndl-datepicker-header", onClick: () => headerAction('year'), children: _jsxs("div", { className: "n-flex n-items-center", children: [_jsx(Typography, { variant: "label", className: "n-p-token-4", children: format(props.date, 'yyyy') }), _jsx(ChevronDownIconOutline, { className: classNames('ndl-datepicker-chevron', {
                                            'n-rotate-180': picker === 'year',
                                        }), "aria-label": "Chevron icon" })] }) })] }), picker !== 'month' && (_jsxs("div", { children: [_jsx(IconButton, { "aria-label": "Previous period", clean: true, onClick: prevCallback, disabled: prevDisabled, children: _jsx(ArrowLeftIconOutline, {}) }), _jsx(IconButton, { "aria-label": "Next period", clean: true, onClick: nextCallback, disabled: nextDisabled, children: _jsx(ArrowRightIconOutline, {}) })] }))] }));
    }, [picker, headerAction]);
    /**
     * Intercept onChange so we can work with
     * Month and Year pickers
     */
    const interceptedChange = useCallback((...args) => {
        if (picker !== 'day') {
            reactDatePickerProps === null || reactDatePickerProps === void 0 ? void 0 : reactDatePickerProps.onChange(...args);
            setPicker('day');
        }
        else {
            reactDatePickerProps === null || reactDatePickerProps === void 0 ? void 0 : reactDatePickerProps.onChange(...args);
        }
    }, [picker, reactDatePickerProps]);
    /**
     * Intercept onCalendarClose so we can
     * switch to "day" picker if we close on "month" or "year"
     * view
     */
    const interceptedOnCalendarClose = useCallback(() => {
        var _a;
        if (picker !== 'day') {
            setPicker('day');
        }
        (_a = reactDatePickerProps === null || reactDatePickerProps === void 0 ? void 0 : reactDatePickerProps.onCalendarClose) === null || _a === void 0 ? void 0 : _a.call(reactDatePickerProps);
    }, [picker, reactDatePickerProps]);
    return (_jsx("div", { className: classes, children: _jsx(ReactDatePicker, Object.assign({ ref: datetimeRef, customInput: _jsx(TextInput, Object.assign({ ref: ref, "aria-label": "Date picker input", rightIcon: _jsx(CalendarIconOutline, { onClick: (e) => {
                        var _a;
                        if ((_a = datetimeRef.current) === null || _a === void 0 ? void 0 : _a.isCalendarOpen()) {
                            datetimeRef.current.setOpen(false);
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }, "aria-label": "Calender Icon" }) }, textInputProps, { disabled: disabled })), disabled: disabled, showMonthYearPicker: picker === 'month', showYearPicker: picker === 'year', shouldCloseOnSelect: picker === 'day', dayClassName: () => 'ndl-datepicker-day', renderCustomHeader: CustomHeader }, reactDatePickerProps, { onChange: interceptedChange, onCalendarClose: interceptedOnCalendarClose })) }));
});
//# sourceMappingURL=DatePicker.js.map